import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getActiveEntitySlug, setActiveEntityBrand } from '../utils/activeEntity';
import BrandThemeEditor from '../components/admin/BrandThemeEditor';
import ShareLinks from '../components/admin/ShareLinks';
import { BODY_FONTS, HEADING_FONTS, findFont, fontStack, loadFont } from '../utils/brandFonts';
import './BrandStrategies.css';
import './BrandSettings.css';

/**
 * My brand (Brand Admins): the brand's own look, alert emails, Whop billing and
 * team, without a Platform Admin. What's editable follows the brand's plan
 * (server: /api/entities/my-brand). The plan itself stays with RiskLo.
 */

const PLAN_LABEL = { starter: 'Starter', brand: 'Brand', partner: 'Partner', whitelabel: 'White-label' };
const MAX_LOGO_BYTES = 512 * 1024;

function authJson() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

function readPng(file) {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== 'image/png') { reject(new Error('The logo must be a PNG.')); return; }
    if (file.size > MAX_LOGO_BYTES) { reject(new Error('The logo must be 512 KB or smaller.')); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Couldn’t read the logo.'));
    reader.readAsDataURL(file);
  });
}

function Locked({ plan }) {
  return <p className="bset-locked">Available on the {PLAN_LABEL[plan]} plan. Ask RiskLo to move you up.</p>;
}

function Team({ onCount }) {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    const res = await fetch(API_ENDPOINTS.myBrandTeam, { headers: getBearerAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (data.success) { setTeam(data.team || []); onCount?.((data.team || []).length); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setNote('');
    const res = await fetch(API_ENDPOINTS.myBrandTeam, { method: 'POST', headers: authJson(), body: JSON.stringify({ email, role }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) { setNote(data.error || 'Couldn’t add them.'); return; }
    setEmail('');
    setNote(`Added. They can sign in with ${email}.`);
    load();
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} (${row.role === 'support' ? 'Support' : 'Member'})?`)) return;
    await fetch(API_ENDPOINTS.myBrandTeam, { method: 'DELETE', headers: authJson(), body: JSON.stringify({ email: row.email, role: row.role }) });
    load();
  };

  return (
    <section className="bset-card" id="bset-team">
      <SectionHead title="Team" sub="People who get your brand without paying on Whop." />
      <p className="bs-muted">
        <strong>Members</strong> get your brand without a Whop membership (staff, partners, comped clients).
        {' '}<strong>Support</strong> also get the support tools (Trade Watcher board, CSAs).
      </p>
      <form className="bset-row" onSubmit={add}>
        <input type="email" required placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="member">Member</option>
          <option value="support">Support</option>
        </select>
        <button type="submit" className="bs-btn bs-btn-primary">Add</button>
      </form>
      {note ? <p className="bs-note" role="status">{note}</p> : null}
      {team && team.length ? (
        <ul className="bset-team">
          {team.map((r) => (
            <li key={`${r.role}:${r.email}`}>
              <span>{r.email}</span>
              <span className="bs-chip is-muted">{r.role === 'support' ? 'Support' : 'Member'}</span>
              <button type="button" className="bs-link" onClick={() => remove(r)}>Remove</button>
            </li>
          ))}
        </ul>
      ) : null}
      {team && !team.length ? <p className="bs-muted bs-small">No one added yet.</p> : null}
    </section>
  );
}

function SectionHead({ title, sub, right }) {
  return (
    <div className="bset-section-head">
      <div>
        <h3>{title}</h3>
        {sub ? <p>{sub}</p> : null}
      </div>
      {right || null}
    </div>
  );
}

const LAYOUTS = [
  { value: 'classic', name: 'Classic', desc: 'Glassy, rounded', radius: 10 },
  { value: 'clean', name: 'Clean', desc: 'Soft, very rounded', radius: 16 },
  { value: 'sharp', name: 'Sharp', desc: 'Square, flat, dense', radius: 2 },
];

const snapshot = (d) => JSON.stringify(d);

/** Brand fonts: headings and body text, each with a live preview. */
function FontPicker({ fonts, onChange, headingStyle = '', onHeadingStyle }) {
  const heading = findFont(HEADING_FONTS, fonts.heading);
  const body = findFont(BODY_FONTS, fonts.body);
  useEffect(() => { loadFont(heading); loadFont(body); }, [heading, body]);
  const set = (slot, key) => {
    const next = { ...fonts, [slot]: key };
    if (key === 'default') delete next[slot];
    onChange(Object.keys(next).length ? next : undefined);
  };
  return (
    <div className="bset-fonts">
      <span className="bte-label bset-layout-label">Fonts</span>
      <div className="bs-grid2">
        <label className="bs-field">
          <span>Headings</span>
          <select value={heading.key} onChange={(e) => set('heading', e.target.value)}>
            {HEADING_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
        <label className="bs-field">
          <span>Body text, numbers and buttons</span>
          <select value={body.key} onChange={(e) => set('body', e.target.value)}>
            {BODY_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
        <label className="bs-field">
          <span>Heading style</span>
          <select value={headingStyle || ''} onChange={(e) => onHeadingStyle(e.target.value || null)}>
            <option value="">Standard</option>
            <option value="tight">Tight: medium weight, tight spacing</option>
          </select>
        </label>
      </div>
      <div className="bset-font-preview" aria-hidden="true">
        <div className="bset-font-preview-h" style={{ fontFamily: fontStack(heading) || fontStack(body) || undefined, ...(headingStyle === 'tight' ? { fontWeight: 500, letterSpacing: '-0.035em' } : {}) }}>Adaptive growth that knows when to play it safe.</div>
        <div className="bset-font-preview-p" style={{ fontFamily: fontStack(body) || undefined }}>Account value $104,382.15 · Today +$1,284.40 (+1.24%) · 9 of 14 days green</div>
      </div>
    </div>
  );
}

export default function BrandSettings() {
  const [brand, setBrand] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [secrets, setSecrets] = useState({ whopApiKey: '', whopWebhookSecret: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [whopTest, setWhopTest] = useState('');
  const [teamCount, setTeamCount] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const toDraft = (br) => ({
    branding: { ...br.branding },
    tradeWatchSupportEmails: (br.tradeWatchSupportEmails || []).join(', '),
    checkoutUrl: br.checkoutUrl || '',
    whopCompanyId: br.whopCompanyId || '',
  });

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.myBrand, { headers: getBearerAuthHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t load your brand');
      setBrand(data.brand);
      const d = toDraft(data.brand);
      setDraft(d);
      setSaved(snapshot(d));
    } catch (err) {
      setError(err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = Boolean(draft) && (snapshot(draft) !== saved || Boolean(secrets.whopApiKey || secrets.whopWebhookSecret));

  // Don't lose edits by closing the tab.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const checklist = useMemo(() => {
    if (!brand || !draft) return [];
    const b = draft.branding;
    const themeOn = Boolean(b.theme?.primary) && b.theme?.enabled !== false;
    return [
      { id: 'bset-logo', label: 'Logo', done: Boolean(b.logoUrl) },
      { id: 'bset-theme', label: 'Colors', done: themeOn, optional: !brand.editable.includes('theme') },
      { id: 'bset-alerts', label: 'Support email', done: Boolean(b.supportEmail) },
      { id: 'bset-billing', label: 'Whop billing', done: Boolean(brand.whop?.apiKeySet && draft.whopCompanyId && draft.checkoutUrl) },
      { id: 'bset-team', label: teamCount ? `Team (${teamCount})` : 'Team', done: Boolean(teamCount) },
    ].filter((c) => !c.optional);
  }, [brand, draft, teamCount]);

  if (error && !brand) return <div className="bs-page"><p className="bs-note is-bad">{error}</p></div>;
  if (!brand || !draft) return <div className="bs-page"><p className="bs-muted">Loading…</p></div>;

  const b = draft.branding;
  const can = (key) => brand.editable.includes(key);
  const setB = (key, value) => setDraft((d) => ({ ...d, branding: { ...d.branding, [key]: value } }));

  const save = async () => {
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const branding = {};
      brand.editable.forEach((k) => { branding[k] = b[k] ?? ''; });
      if (can('theme')) branding.theme = b.theme && b.theme.primary ? b.theme : null;
      if (can('layout')) branding.layout = b.layout || { preset: 'classic' };
      const res = await fetch(API_ENDPOINTS.myBrand, {
        method: 'PUT',
        headers: authJson(),
        body: JSON.stringify({
          branding,
          tradeWatchSupportEmails: draft.tradeWatchSupportEmails,
          checkoutUrl: draft.checkoutUrl,
          whopCompanyId: draft.whopCompanyId,
          ...(secrets.whopApiKey ? { whopApiKey: secrets.whopApiKey } : {}),
          ...(secrets.whopWebhookSecret ? { whopWebhookSecret: secrets.whopWebhookSecret } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t save');
      setBrand(data.brand);
      const d = toDraft(data.brand);
      setDraft(d);
      setSaved(snapshot(d));
      setSecrets({ whopApiKey: '', whopWebhookSecret: '' });
      if (data.preview && getActiveEntitySlug() === data.brand.slug) setActiveEntityBrand(data.preview);
      setStatus('Saved. Your members see the changes straight away.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setDraft(JSON.parse(saved));
    setResetKey((k) => k + 1);
    setSecrets({ whopApiKey: '', whopWebhookSecret: '' });
    setError('');
    setStatus('');
  };

  const testWhop = async () => {
    setWhopTest('Checking with Whop…');
    try {
      const res = await fetch(API_ENDPOINTS.myBrandTestWhop, {
        method: 'POST',
        headers: authJson(),
        body: JSON.stringify({ whopCompanyId: draft.whopCompanyId, whopApiKey: secrets.whopApiKey || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Couldn’t reach Whop');
      setWhopTest(data.message || (data.ok ? 'Connected.' : 'Whop didn’t accept those details.'));
    } catch (err) {
      setWhopTest(err.message);
    }
  };

  const applyLogoFile = async (file) => {
    if (!file) return;
    setError('');
    try { setB('logoUrl', await readPng(file)); } catch (err) { setError(err.message); }
  };
  const pickLogo = (e) => { const f = e.target.files?.[0]; e.target.value = ''; applyLogoFile(f); };
  const dropLogo = (e) => { e.preventDefault(); setDragging(false); applyLogoFile(e.dataTransfer.files?.[0]); };
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <div className="bs-page bset-page">
      <div className="bset-hero">
        <div className="bset-hero-id">
          {b.logoUrl ? <img src={b.logoUrl} alt="" /> : <span className="bset-hero-initial">{(brand.displayName || 'B')[0]}</span>}
          <div>
            <h1>Brand settings</h1>
            <p className="bs-muted">
              <strong>{brand.displayName}</strong> <span className="bset-plan">{PLAN_LABEL[brand.plan]} plan</span>
            </p>
          </div>
        </div>
        <button type="button" className="bs-btn bs-btn-primary bs-btn-lg" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'All saved'}
        </button>
      </div>

      <div className="bset-checklist" aria-label="Setup">
        <span className="bset-checklist-count">{doneCount} of {checklist.length} set up</span>
        {checklist.map((c) => (
          <button key={c.id} type="button" className={`bset-check${c.done ? ' is-done' : ''}`} onClick={() => jump(c.id)}>
            <span className="bset-check-dot" aria-hidden="true">{c.done ? '✓' : ''}</span>{c.label}
          </button>
        ))}
      </div>

      {status ? <p className="bs-note is-ok" role="status">{status}</p> : null}
      {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}

      {brand.unlocked?.partner ? (
        <section className="bset-card bset-link-card" id="bset-link">
          <SectionHead title="Your link" sub="Share this with your members. It opens your branded sign-in and lands them in your workspace." />
          <span className="bset-copy">
            <input readOnly value={`${window.location.origin.replace(/^https?:\/\/(www\.)?/, '')}/${brand.slug}`} onFocus={(e) => e.target.select()} />
            <button type="button" className="bs-btn" onClick={() => { try { navigator.clipboard.writeText(`${window.location.origin}/${brand.slug}`); setStatus('Link copied.'); } catch (_e) { /* clipboard blocked */ } }}>Copy</button>
          </span>
        </section>
      ) : null}

      <section className="bset-card" id="bset-logo">
        <SectionHead title="Logo & name" sub="Shown in the sidebar, the brand switcher and your members’ emails." />
        <div className="bset-logo-row">
          <div
            className={`bset-drop${dragging ? ' is-drag' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={dropLogo}
            onClick={() => fileRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
            aria-label="Upload logo"
          >
            {b.logoUrl ? <img src={b.logoUrl} alt="Your logo" /> : <span>Drop a PNG<br />or click</span>}
          </div>
          <div className="bset-logo-side">
            <div className="bset-logo-actions">
              <button type="button" className="bs-btn" onClick={() => fileRef.current?.click()}>{b.logoUrl ? 'Replace logo' : 'Upload logo'}</button>
              {b.logoUrl ? <button type="button" className="bs-btn bset-btn-quiet" onClick={() => setB('logoUrl', '')}>Remove</button> : null}
              <input ref={fileRef} type="file" accept="image/png" onChange={pickLogo} hidden />
            </div>
            <small className="bs-muted bs-small">PNG, square works best, up to 512 KB. Transparent backgrounds look best on dark and light.</small>
          </div>
        </div>
        <div className="bs-grid2">
          <label className="bs-field">
            <span>Results menu label</span>
            <input placeholder={`${brand.displayName} Results`} value={b.resultsLabel || ''} onChange={(e) => setB('resultsLabel', e.target.value)} />
            <small className="bs-muted bs-small">What the Results page is called in your members’ menu.</small>
          </label>
          <label className="bs-field">
            <span>Logo badge color</span>
            <span className="bset-color-row">
              <input type="color" value={/^#[0-9a-f]{6}$/i.test(b.accentColor || '') ? b.accentColor : (b.theme?.primary || '#667eea')} onChange={(e) => setB('accentColor', e.target.value)} aria-label="Logo badge color picker" />
              <input placeholder="Same as your main color" value={b.accentColor || ''} onChange={(e) => setB('accentColor', e.target.value.trim())} maxLength={7} />
            </span>
            <small className="bs-muted bs-small">The square behind your logo in the brand switcher. Blank uses your main color.</small>
          </label>
        </div>
      </section>

      <section className="bset-card" id="bset-theme">
        <SectionHead title="Colors & layout" sub="How the app looks for your members. Other brands are unaffected." />
        {can('theme') ? (
          <>
            <BrandThemeEditor key={resetKey} layout={b.layout?.preset || 'classic'} fonts={b.layout?.fonts || null} headingStyle={b.layout?.headingStyle || null} theme={b.theme} brandName={brand.displayName} logoUrl={b.logoUrl || ''} onChange={(next) => setB('theme', next)} />
            <span className="bte-label bset-layout-label">Layout style</span>
            <div className="bset-layouts" role="radiogroup" aria-label="Layout style">
              {LAYOUTS.map((l) => {
                const on = (b.layout?.preset || 'classic') === l.value;
                return (
                  <button key={l.value} type="button" role="radio" aria-checked={on} className={`bset-layout${on ? ' is-on' : ''}`} onClick={() => setB('layout', { ...(b.layout || {}), preset: l.value })}>
                    <span className="bset-layout-art" aria-hidden="true">
                      <i style={{ borderRadius: l.radius }} /><i style={{ borderRadius: l.radius }} /><i style={{ borderRadius: l.radius }} />
                    </span>
                    <strong>{l.name}</strong>
                    <small>{l.desc}</small>
                  </button>
                );
              })}
            </div>
            <FontPicker
              fonts={b.layout?.fonts || {}}
              onChange={(fonts) => setB('layout', { ...(b.layout || { preset: 'classic' }), fonts })}
              headingStyle={b.layout?.headingStyle || ''}
              onHeadingStyle={(value) => {
                const { headingStyle, ...rest } = b.layout || { preset: 'classic' };
                setB('layout', value ? { ...rest, headingStyle: value } : rest);
              }}
            />
            <span className="bte-label bset-layout-label">First look for new members</span>
            <div className="bset-modes" role="radiogroup" aria-label="Default appearance">
              {[['', 'Their choice', 'Dark until they switch'], ['dark', 'Dark', 'Opens in dark mode'], ['light', 'Light', 'Opens in light mode']].map(([value, name, desc]) => {
                const on = (b.layout?.defaultMode || '') === value;
                return (
                  <button
                    key={value || 'none'}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`bset-mode${on ? ' is-on' : ''}`}
                    onClick={() => {
                      const { defaultMode, ...rest } = b.layout || { preset: 'classic' };
                      setB('layout', value ? { ...rest, defaultMode: value } : rest);
                    }}
                  >
                    <strong>{name}</strong>
                    <small>{desc}</small>
                  </button>
                );
              })}
            </div>
            <small className="bs-muted bs-small">A member’s own light/dark choice always wins.</small>
          </>
        ) : <Locked plan="brand" />}
      </section>

      <section className="bset-card" id="bset-name">
        <SectionHead title="Your own app name" sub="Replace “RiskLo” with your own name everywhere (white-label)." />
        {can('appName') ? (
          <div className="bs-grid2">
            {[['appName', 'App name'], ['tagline', 'Tagline'], ['legalName', 'Legal name'], ['emailFromName', 'Email sender name']].map(([k, label]) => (
              <label key={k} className="bs-field"><span>{label}</span><input value={b[k] || ''} onChange={(e) => setB(k, e.target.value)} /></label>
            ))}
          </div>
        ) : <Locked plan="whitelabel" />}
      </section>

      <section className="bset-card" id="bset-alerts">
        <SectionHead title="Support & alerts" sub="Where members reach you, and who gets copies of trade alerts." />
        <div className="bs-grid2">
          <label className="bs-field"><span>Support email (shown to members)</span><input type="email" placeholder="support@yourco.com" value={b.supportEmail || ''} onChange={(e) => setB('supportEmail', e.target.value)} /></label>
          <label className="bs-field">
            <span>Trade alert copies go to</span>
            <input placeholder="support@yourco.com, desk@yourco.com" value={draft.tradeWatchSupportEmails} onChange={(e) => setDraft((d) => ({ ...d, tradeWatchSupportEmails: e.target.value }))} />
            <small className="bs-muted bs-small">Separate several emails with commas.</small>
          </label>
        </div>
      </section>

      <section className="bset-card" id="bset-billing">
        <SectionHead
          title="Billing (Whop)"
          sub="Your members pay you on Whop; RiskLo gives them access while their membership is active."
          right={brand.whop.apiKeySet ? <span className="bset-pill is-ok">Connected</span> : <span className="bset-pill">Not connected</span>}
        />
        <div className="bs-grid2">
          <label className="bs-field"><span>Whop company ID</span><input placeholder="biz_…" value={draft.whopCompanyId} onChange={(e) => setDraft((d) => ({ ...d, whopCompanyId: e.target.value }))} /></label>
          <label className="bs-field"><span>Checkout link</span><input placeholder="https://whop.com/…" value={draft.checkoutUrl} onChange={(e) => setDraft((d) => ({ ...d, checkoutUrl: e.target.value }))} /></label>
          <label className="bs-field">
            <span>API key</span>
            <input type="password" autoComplete="off" placeholder={brand.whop.apiKeySet ? `Saved · ending ${brand.whop.apiKeyLast4}` : 'Paste your Whop API key'} value={secrets.whopApiKey} onChange={(e) => setSecrets((s) => ({ ...s, whopApiKey: e.target.value }))} />
          </label>
          <label className="bs-field">
            <span>Webhook secret</span>
            <input type="password" autoComplete="off" placeholder={brand.whop.webhookSecretSet ? `Saved · ending ${brand.whop.webhookSecretLast4}` : 'ws_…'} value={secrets.whopWebhookSecret} onChange={(e) => setSecrets((s) => ({ ...s, whopWebhookSecret: e.target.value }))} />
          </label>
        </div>
        <label className="bs-field">
          <span>In Whop, add a webhook pointing here</span>
          <span className="bset-copy">
            <input readOnly value={brand.whop.webhookUrl} onFocus={(e) => e.target.select()} />
            <button type="button" className="bs-btn" onClick={() => { try { navigator.clipboard.writeText(brand.whop.webhookUrl); setWhopTest('Webhook address copied.'); } catch (_e) { /* clipboard blocked */ } }}>Copy</button>
          </span>
        </label>
        <div className="bs-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="bs-btn" onClick={testWhop}>Test connection</button>
        </div>
        {whopTest ? <p className="bs-note" role="status">{whopTest}</p> : null}
        <p className="bs-muted bs-small">Keys are stored encrypted and never shown again; leave a field empty to keep what’s saved.</p>
      </section>

      <ShareLinks brandName={brand.displayName} />

      <Team onCount={setTeamCount} />

      {dirty ? (
        <div className="bset-savebar" role="region" aria-label="Unsaved changes">
          <span>You have unsaved changes</span>
          <button type="button" className="bs-btn bset-btn-quiet" onClick={discard} disabled={saving}>Discard</button>
          <button type="button" className="bs-btn bs-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      ) : null}
    </div>
  );
}
